import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Eye, UserPlus } from "lucide-react";

import { adminApi, adminSettingsApi } from "@profitlens/api-client";
import type { AdminTenantOut, Plan, TenantStatus } from "@profitlens/api-client";
import { Badge } from "@profitlens/ui/badge";
import { Button } from "@profitlens/ui/button";
import { Card } from "@profitlens/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@profitlens/ui/dialog";
import { ErrorState } from "@profitlens/ui/error-state";
import { Input } from "@profitlens/ui/input";
import { Label } from "@profitlens/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@profitlens/ui/select";
import { Spinner } from "@profitlens/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@profitlens/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@profitlens/ui/tabs";

import { PageHeader } from "@/components/page-header";
import { Pagination } from "@/components/pagination";
import { translateError } from "@/lib/api-error";
import { formatDateTime } from "@/lib/format";

const PAGE_SIZE = 20;

export function TenantsPage() {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState<AdminTenantOut | null>(null);
  const [planTarget, setPlanTarget] = useState<AdminTenantOut | null>(null);
  const [statusTarget, setStatusTarget] = useState<AdminTenantOut | null>(null);

  const list = useQuery({
    queryKey: ["admin", "tenants", { q, page }],
    queryFn: () => adminApi.listTenants({ q: q || undefined, page, page_size: PAGE_SIZE }),
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title={t("tenants.title")}
        subtitle={t("tenants.subtitle")}
        actions={
          <Input
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            placeholder={t("tenants.searchPlaceholder")}
            className="w-64"
          />
        }
      />

      {list.isLoading ? (
        <div className="grid place-items-center py-12">
          <Spinner className="h-5 w-5" />
        </div>
      ) : list.error ? (
        <ErrorState error={list.error} onRetry={() => list.refetch()} />
      ) : list.data && list.data.items.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted">{t("tenants.empty")}</Card>
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("tenants.colTenant")}</TableHead>
                <TableHead>{t("tenants.colPlan")}</TableHead>
                <TableHead>{t("tenants.colStatus")}</TableHead>
                <TableHead>{t("tenants.colMembers")}</TableHead>
                <TableHead>{t("tenants.colCurrency")}</TableHead>
                <TableHead>{t("tenants.colCreatedAt")}</TableHead>
                <TableHead className="w-56">{t("common.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.data?.items.map((tenant) => (
                <TableRow key={tenant.id}>
                  <TableCell className="font-medium">{tenant.name}</TableCell>
                  <TableCell>
                    <PlanBadge plan={tenant.effective_plan} />
                    {tenant.effective_plan !== tenant.plan ? (
                      <span className="ml-1 text-xs text-muted">
                        {t("tenants.paidPlan", { plan: tenant.plan })}
                      </span>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={tenant.status} />
                  </TableCell>
                  <TableCell className="tabular-nums">{tenant.members}</TableCell>
                  <TableCell>{tenant.currency}</TableCell>
                  <TableCell className="text-xs whitespace-nowrap text-muted">
                    {formatDateTime(tenant.created_at)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <Button size="sm" variant="outline" onClick={() => setDetail(tenant)}>
                        <Eye className="h-3.5 w-3.5" />
                        {t("common.detail")}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setPlanTarget(tenant)}>
                        {t("tenants.planAction")}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setStatusTarget(tenant)}>
                        {t("tenants.statusAction")}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={list.data?.total ?? 0}
            onPage={setPage}
          />
        </Card>
      )}

      <TenantDetailDialog tenant={detail} onClose={() => setDetail(null)} />
      <PlanDialog tenant={planTarget} onClose={() => setPlanTarget(null)} />
      <StatusDialog tenant={statusTarget} onClose={() => setStatusTarget(null)} />
    </div>
  );
}

function PlanBadge({ plan }: { plan: Plan }) {
  const { t } = useTranslation();
  if (plan === "pro") return <Badge>{t("tenants.planPro")}</Badge>;
  return <Badge variant="secondary">{t("tenants.planFree")}</Badge>;
}

function StatusBadge({ status }: { status: TenantStatus }) {
  const { t } = useTranslation();
  if (status === "active") return <Badge variant="success">{t("tenants.statusActive")}</Badge>;
  if (status === "suspended")
    return <Badge variant="warning">{t("tenants.statusSuspended")}</Badge>;
  return <Badge variant="danger">{t("tenants.statusClosed")}</Badge>;
}

// ---- 详情：基本信息 + 成员管理 ----

function TenantDetailDialog({
  tenant,
  onClose,
}: {
  tenant: AdminTenantOut | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Dialog open={tenant !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {t("tenants.detailTitle")} · {tenant?.name}
          </DialogTitle>
        </DialogHeader>
        {tenant ? (
          <Tabs defaultValue="info">
            <TabsList>
              <TabsTrigger value="info">{t("tenants.tabInfo")}</TabsTrigger>
              <TabsTrigger value="members">{t("tenants.tabMembers")}</TabsTrigger>
            </TabsList>
            <TabsContent value="info">
              <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
                <InfoRow label={t("tenants.infoName")} value={tenant.name} />
                <InfoRow label={t("tenants.infoId")} value={tenant.id} mono />
                <InfoRow
                  label={t("tenants.infoPlan")}
                  value={tenant.plan === "pro" ? t("tenants.planPro") : t("tenants.planFree")}
                />
                <InfoRow
                  label={t("tenants.infoEffectivePlan")}
                  value={
                    tenant.effective_plan === "pro" ? t("tenants.planPro") : t("tenants.planFree")
                  }
                />
                <InfoRow
                  label={t("tenants.infoTrialEnds")}
                  value={
                    tenant.trial_ends_at ? formatDateTime(tenant.trial_ends_at) : t("common.never")
                  }
                />
                <InfoRow
                  label={t("tenants.infoStatus")}
                  value={
                    tenant.status === "active"
                      ? t("tenants.statusActive")
                      : tenant.status === "suspended"
                        ? t("tenants.statusSuspended")
                        : t("tenants.statusClosed")
                  }
                />
                <InfoRow label={t("common.currency")} value={tenant.currency} />
                <InfoRow label={t("common.createdAt")} value={formatDateTime(tenant.created_at)} />
              </dl>
            </TabsContent>
            <TabsContent value="members">
              <MembersTab tenant={tenant} />
            </TabsContent>
          </Tabs>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function InfoRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <dt className="text-xs text-muted">{label}</dt>
      <dd className={mono ? "mt-0.5 font-mono text-xs break-all" : "mt-0.5 font-medium"}>
        {value}
      </dd>
    </div>
  );
}

function MembersTab({ tenant }: { tenant: AdminTenantOut }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<"owner" | "member">("member");
  const [error, setError] = useState<string | null>(null);

  const members = useQuery({
    queryKey: ["admin", "tenants", tenant.id, "members"],
    queryFn: () => adminSettingsApi.listMembers(tenant.id),
  });

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["admin", "tenants", tenant.id, "members"] }),
      queryClient.invalidateQueries({ queryKey: ["admin", "tenants"] }),
    ]);

  const add = useMutation({
    mutationFn: () => adminSettingsApi.addMember(tenant.id, { email, password, role }),
    onSuccess: async () => {
      setEmail("");
      setPassword("");
      setRole("member");
      setError(null);
      await invalidate();
    },
    onError: (err) => setError(translateError(err, t)),
  });

  const patchRole = useMutation({
    mutationFn: ({ userId, role: nextRole }: { userId: string; role: "owner" | "member" }) =>
      adminSettingsApi.patchMember(tenant.id, userId, { role: nextRole }),
    onSuccess: invalidate,
    onError: (err) => setError(translateError(err, t)),
  });

  const remove = useMutation({
    mutationFn: (userId: string) => adminSettingsApi.removeMember(tenant.id, userId),
    onSuccess: invalidate,
    onError: (err) => setError(translateError(err, t)),
  });

  return (
    <div className="space-y-4">
      {members.isLoading ? (
        <div className="grid place-items-center py-8">
          <Spinner className="h-5 w-5" />
        </div>
      ) : members.error ? (
        <ErrorState error={members.error} onRetry={() => members.refetch()} />
      ) : members.data && members.data.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted">{t("tenants.membersEmpty")}</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("common.email")}</TableHead>
              <TableHead>{t("common.role")}</TableHead>
              <TableHead>{t("common.lastLoginAt")}</TableHead>
              <TableHead className="w-44">{t("common.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {members.data?.map((m) => (
              <TableRow key={m.id}>
                <TableCell className="font-medium">{m.email}</TableCell>
                <TableCell>
                  {m.role === "owner" ? (
                    <Badge>{t("tenants.roleOwner")}</Badge>
                  ) : (
                    <Badge variant="secondary">{t("tenants.roleMember")}</Badge>
                  )}
                </TableCell>
                <TableCell className="text-xs text-muted">
                  {m.last_login_at ? formatDateTime(m.last_login_at) : t("common.never")}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={patchRole.isPending}
                      onClick={() =>
                        patchRole.mutate({
                          userId: m.id,
                          role: m.role === "owner" ? "member" : "owner",
                        })
                      }
                    >
                      {m.role === "owner" ? t("tenants.roleMember") : t("tenants.roleOwner")}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={remove.isPending}
                      onClick={() => {
                        if (window.confirm(t("tenants.memberRemoveConfirm"))) {
                          remove.mutate(m.id);
                        }
                      }}
                    >
                      {t("tenants.memberRemove")}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <form
        className="space-y-3 rounded-lg border border-border bg-surface-muted/50 p-3"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          add.mutate();
        }}
      >
        <p className="flex items-center gap-1.5 text-sm font-medium">
          <UserPlus className="h-4 w-4 text-brand" />
          {t("tenants.memberAdd")}
        </p>
        <div className="grid gap-2 sm:grid-cols-[1fr_1fr_140px_auto]">
          <Input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("tenants.memberEmail")}
          />
          <Input
            type="text"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t("tenants.memberPassword")}
            title={t("tenants.memberPasswordHint")}
          />
          <Select value={role} onValueChange={(v) => setRole(v as "owner" | "member")}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="member">{t("tenants.roleMember")}</SelectItem>
              <SelectItem value="owner">{t("tenants.roleOwner")}</SelectItem>
            </SelectContent>
          </Select>
          <Button type="submit" disabled={add.isPending || !email || password.length < 8}>
            {add.isPending ? t("common.processing") : t("common.add")}
          </Button>
        </div>
        {error ? <p className="text-xs text-danger">{error}</p> : null}
      </form>
    </div>
  );
}

// ---- 调整套餐 ----

function PlanDialog({ tenant, onClose }: { tenant: AdminTenantOut | null; onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [plan, setPlan] = useState<Plan>("pro");
  const [trialDays, setTrialDays] = useState("14");
  const [error, setError] = useState<string | null>(null);

  const update = useMutation({
    mutationFn: () => {
      if (!tenant) throw new Error("no tenant");
      const days = plan === "pro" ? Math.max(0, Number.parseInt(trialDays, 10) || 0) : 0;
      return adminApi.updatePlan(tenant.id, {
        plan,
        days: 30,
        extend_trial_days: days > 0 ? days : null,
        provider: "manual",
      });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin", "tenants"] }),
        queryClient.invalidateQueries({ queryKey: ["admin", "stats"] }),
        queryClient.invalidateQueries({ queryKey: ["admin", "audit-logs"] }),
      ]);
      onClose();
    },
    onError: (err) => setError(translateError(err, t)),
  });

  return (
    <Dialog open={tenant !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("tenants.planDialogTitle", { name: tenant?.name })}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>{t("tenants.planField")}</Label>
            <Select value={plan} onValueChange={(v) => setPlan(v as Plan)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="free">{t("tenants.planFree")}</SelectItem>
                <SelectItem value="pro">{t("tenants.planPro")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {plan === "pro" ? (
            <div className="space-y-1.5">
              <Label>{t("tenants.planTrialDays")}</Label>
              <Input
                type="number"
                min={0}
                value={trialDays}
                onChange={(e) => setTrialDays(e.target.value)}
              />
              <p className="text-xs text-muted">{t("tenants.planTrialDaysHint")}</p>
            </div>
          ) : null}
          {error ? <p className="text-xs text-danger">{error}</p> : null}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button disabled={update.isPending || !tenant} onClick={() => update.mutate()}>
            {update.isPending ? t("common.processing") : t("common.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---- 启停 ----

function StatusDialog({ tenant, onClose }: { tenant: AdminTenantOut | null; onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<TenantStatus>("suspended");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const update = useMutation({
    mutationFn: () => {
      if (!tenant) throw new Error("no tenant");
      return adminApi.updateStatus(tenant.id, { status, reason: reason || null });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin", "tenants"] }),
        queryClient.invalidateQueries({ queryKey: ["admin", "stats"] }),
        queryClient.invalidateQueries({ queryKey: ["admin", "audit-logs"] }),
      ]);
      setReason("");
      onClose();
    },
    onError: (err) => setError(translateError(err, t)),
  });

  return (
    <Dialog open={tenant !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("tenants.statusDialogTitle", { name: tenant?.name })}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>{t("tenants.statusTarget")}</Label>
            <Select value={status} onValueChange={(v) => setStatus(v as TenantStatus)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">{t("tenants.statusActive")}</SelectItem>
                <SelectItem value="suspended">{t("tenants.statusSuspended")}</SelectItem>
                <SelectItem value="closed">{t("tenants.statusClosed")}</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted">{t("tenants.statusWarning")}</p>
          </div>
          <div className="space-y-1.5">
            <Label>{t("tenants.statusReason")}</Label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t("tenants.statusReasonPlaceholder")}
            />
          </div>
          {error ? <p className="text-xs text-danger">{error}</p> : null}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            variant={status === "active" ? "default" : "danger"}
            disabled={update.isPending || !tenant}
            onClick={() => update.mutate()}
          >
            {update.isPending ? t("common.processing") : t("common.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
